import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import posthog from "posthog-js";
import { CONFIG } from "../config";

const VehicleAgeGraph = ({ selectedDate, selectedStatus, selectedAgeRange, onAgeRangeChange }) => {
  const chartRef = useRef();
  const containerRef = useRef();
  const [vehicleAges, setVehicleAges] = useState([]);
  const [avgAge, setAvgAge] = useState(null);
  const [chartWidth, setChartWidth] = useState(452);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setChartWidth(containerRef.current.offsetWidth - 5);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const fetchVehicleAges = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${CONFIG.API_BASE_URL}/${CONFIG.VEHICLE_AVERAGE_AGE_GRAPH_URL}/${localStorage.getItem(
            "tensor-user-id"
          )}?date_str=${selectedDate}&selected_status=${selectedStatus}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );

        const data = await response.json();
        if (data.success && data.data?.vehicles?.length) {
          setVehicleAges(data.data.vehicles);
          setAvgAge(data.data.average_age);
        }
      } catch (err) {
        console.error("Failed to fetch vehicle ages:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleAges();
  }, [selectedDate, selectedStatus]);

  useEffect(() => {
    if (vehicleAges.length === 0 || !chartWidth) return;

    const margin = { top: 60, right: 20, bottom: 50, left: 60 };
    const width = chartWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    d3.select(chartRef.current).selectAll("*").remove();

    const svg = d3
      .select(chartRef.current)
      .append("svg")
      .attr("width", chartWidth)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const binSize = 2;
    const maxAge = d3.max(vehicleAges);
    const binRanges = d3.range(0, maxAge + binSize, binSize);

    const bins = binRanges.map((start, i) => {
      const end = start + binSize;
      const count = vehicleAges.filter((age) =>
        i === 0 ? age >= start && age <= end : age > start && age <= end
      ).length;
      return { x0: start, x1: end, length: count };
    });

    const validRanges = bins.map(b => `${b.x0}-${b.x1}`);
    const isValidSelectedRange = validRanges.includes(selectedAgeRange);

    const xScale = d3.scaleLinear().domain([0, d3.max(binRanges)]).range([0, width]);
    const yScale = d3.scaleLinear().domain([0, d3.max(bins, (d) => d.length)]).range([height, 0]).nice();

    const bar = g
      .selectAll(".bar")
      .data(bins)
      .enter()
      .append("g")
      .attr("class", "bar")
      .attr("transform", (d) => `translate(${xScale(d.x0)}, ${yScale(d.length)})`);

    bar
      .append("rect")
      .attr("x", 1)
      .attr("width", (d) => Math.max(0, xScale(d.x1) - xScale(d.x0) - 0.25))
      .attr("height", (d) => height - yScale(d.length))
      .attr("y", 0)
      .style("cursor", "pointer")
      .style("fill", (d) => {
        const rangeKey = `${d.x0}-${d.x1}`;
        return selectedAgeRange === rangeKey ? "#032FCE" : "#869BE8";
      })
      .on("click", (event, d) => {
        const clickedRange = `${d.x0}-${d.x1}`;
        if (onAgeRangeChange) {
          onAgeRangeChange(clickedRange);
        }
        posthog.capture("vehicles_age_filter_applied", {
          selected_range: clickedRange,
        });
      });

    bar
      .append("text")
      .attr("dy", "-0.5em")
      .attr("x", (d) => (xScale(d.x1) - xScale(d.x0)) / 2)
      .attr("y", 0)
      .attr("text-anchor", "middle")
      .style("font-size", "12px")
      .style("font-style", "normal")
      .style("opacity", 0)
      .style("fill", "rgba(0, 0, 0, 0.70)")
      .text((d) => (d.length > 0 ? d.length : ""))
      .transition()
      .duration(500)
      .style("opacity", 1)
      .style("font-weight", "400")
      .style("line-height", "normal")
      .attr("y", -5);

    g.append("text")
      .attr("x", width / 2)
      .attr("y", height + 45)
      .style("text-anchor", "middle")
      .style("fill", "rgba(0, 0, 0, 0.70)")
      .style("font-size", "12px")
      .text("Age of vehicles (yrs)");

    g.append("g")
      .attr("transform", `translate(0, ${height})`)
      .call(d3.axisBottom(xScale).tickSizeOuter(0))
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", "#333");

    g.append("g")
      .call(d3.axisLeft(yScale).tickSizeOuter(0))
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", "#333");

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", -45)
      .style("text-anchor", "middle")
      .style("fill", "rgba(0, 0, 0, 0.70)")
      .style("font-size", "12px")
      .text("Number of vehicles");

  }, [vehicleAges, avgAge, chartWidth, selectedStatus, selectedAgeRange]);

  return (
    <div ref={containerRef}>
      {loading && (
        <div className="flex justify-center items-center min-h-[220px]">
          <svg className="w-6 h-6 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
            <path className="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z">
            </path>
          </svg>
        </div>
      )}

      {!loading && avgAge !== null && (
        <>
          <h4 className="text-4xl font-extrabold text-gray-900 mb-2 avg_age">
            {avgAge} <span className="text-lg font-medium text-gray-600 avg_age_inner">yrs</span>
          </h4>
          <div ref={chartRef} className="w-full overflow-x-auto"></div>
        </>
      )}
    </div>
  );
};

export default VehicleAgeGraph;
